import { patchUpdateDocument } from 'services/api/dynamicFormsApi';
import createEventEmitter from 'util/createEventEmitter';
import { getApiErrorStatus } from 'util/apiErrorUtils';
import { getEpdWriteBlockedMessage } from 'util/epdActionErrorUtils';
import createPromiseQueue from './createPromiseQueue';

type Rfc6902Patch = {
  path: string;
  op: 'replace' | 'remove' | 'add';
  value?: any;
};

// `message` is the server's own text when the save was refused because the EPD cannot be written
// (409 company locked or Read-only EPD, 404 unknown or Hidden EPD); undefined for any other failure.
export type SyncStatusEvent =
  | { type: 'idle' }
  | { type: 'queued' }
  | { type: 'flushing' }
  | { type: 'error'; error: Error; message?: string };

// A refused write (409 company locked or Read-only EPD, 404 unknown or Hidden EPD) fails the same way on every
// attempt: it is reported at once instead of retried, and its patches are dropped instead of re-queued.
const REFUSED_WRITE_STATUSES = [404, 409];

const isRefusedWrite = (error: unknown): boolean => {
  const status = getApiErrorStatus(error);
  return status !== undefined && REFUSED_WRITE_STATUSES.includes(status);
};

type BatcherOptions = {
  throttleMs: number;
  retryDelaysMs: number[];
  synchronize: ReturnType<typeof createPromiseQueue>;
  onPatchesFlushed?: (patches: readonly Rfc6902Patch[]) => void;
  // Called after a refused write has dropped the patches, so the caller reloads the document and the
  // server's state flags disable the fields.
  onWriteRefused?: () => void;
};

export default function createPatchRequestsBatcher(documentId: string, options: BatcherOptions) {
  let pendingPatches: Rfc6902Patch[] = [];
  let isProcessing: boolean = false;
  const statusEmitter = createEventEmitter<SyncStatusEvent>();

  const flush = async (flushedPatches: Rfc6902Patch[] = []) => {
    const patchesToFlush = pendingPatches;
    pendingPatches = [];

    statusEmitter.emitEvent({ type: 'flushing' });
    try {
      await retryWithDelays(() => patchUpdateDocument(documentId, patchesToFlush), options.retryDelaysMs);
    } catch (error) {
      const isRefused = isRefusedWrite(error);
      // A refused save is final: neither the refused patches nor the edits made meanwhile are ever sent.
      // Any other failure keeps its patches for the next edit to retry.
      pendingPatches = isRefused ? [] : [...patchesToFlush, ...pendingPatches];
      if (error instanceof Error) {
        statusEmitter.emitEvent({ type: 'error', error, message: getEpdWriteBlockedMessage(error) ?? undefined });
      }
      if (isRefused) {
        options.onWriteRefused?.();
      }
      return; // stop flushing batches until the user makes another edit
    }
    flushedPatches.push(...patchesToFlush);

    if (pendingPatches.length > 0) {
      statusEmitter.emitEvent({ type: 'queued' });
      await sleep(options.throttleMs);
      await flush(flushedPatches);
    } else {
      statusEmitter.emitEvent({ type: 'idle' });
      options.onPatchesFlushed?.(flushedPatches);
    }
  };

  return {
    enqueuePatches(patches: Rfc6902Patch[]) {
      if (patches.length === 0) {
        return;
      }
      for (const newPatch of patches) {
        // In case we're typing into the same text field continuously, we want to merge those operations into one
        addToPatchesListAndPossiblyMergeOperations(pendingPatches, newPatch);
      }
      if (!isProcessing) {
        isProcessing = true;
        options
          .synchronize(async () => {
            statusEmitter.emitEvent({ type: 'queued' });
            await sleep(options.throttleMs);
            await flush();
          })
          .finally(() => {
            isProcessing = false;
          });
      }
    },
    statusEmitter,
  };
}

function retryWithDelays<T>(callback: () => Promise<T>, delaysMs: number[]): Promise<T> {
  let attempt = 0;
  return new Promise<T>((resolve, reject) => {
    const tryAttempt = () => {
      callback()
        .then(resolve)
        .catch((error) => {
          if (!isRefusedWrite(error) && attempt < delaysMs.length) {
            const delay = delaysMs[attempt];
            attempt++;
            setTimeout(tryAttempt, delay);
          } else {
            reject(error);
          }
        });
    };

    tryAttempt();
  });
}

function addToPatchesListAndPossiblyMergeOperations(patchesList: Rfc6902Patch[], newPatch: Rfc6902Patch): void {
  const latestPatch = patchesList[patchesList.length - 1];
  if (!latestPatch) {
    patchesList.push(newPatch);
    return;
  }

  if (latestPatch.op === 'replace' && newPatch.op === 'replace' && latestPatch.path === newPatch.path) {
    patchesList[patchesList.length - 1] = newPatch;
    return;
  }

  patchesList.push(newPatch);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
